import React from 'react'
import { icons } from '@enamelai/design-system/icons'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './IconsShowcase.css'

type IconShowcaseEntry = {
  id: string
  label: string
  key: keyof typeof icons
  /** Prefer React component (e.g. Dashboard blocked as <img> by ad blockers). */
  useIcon?: boolean
}

const ICON_GROUPS: Array<{
  id: string
  title: string
  icons: IconShowcaseEntry[]
}> = [
  {
    id: 'operations',
    title: 'Operations',
    icons: [
      { id: 'add_outline', label: 'Add_outline', key: 'add_outline' },
      { id: 'add_filled', label: 'Add', key: 'add_filled' },
      { id: 'add_2', label: 'Add_2', key: 'Add_2' },
      {
        id: 'multiply_outline',
        label: 'Multiply_outline',
        key: 'multiply_outline',
      },
      {
        id: 'multiply_filled',
        label: 'Multiply',
        key: 'multiply_filled',
      },
      {
        id: 'subtraction_outline',
        label: 'Subtraction_outline',
        key: 'subtraction_outline',
      },
      {
        id: 'subtraction_filled',
        label: 'Subtraction',
        key: 'subtraction_filled',
      },
      {
        id: 'division_outline',
        label: 'Division_outline',
        key: 'division_outline',
      },
      {
        id: 'division_filled',
        label: 'Division',
        key: 'division_filled',
      },
    ],
  },
  {
    id: 'arrows',
    title: 'Arrows',
    icons: [
      { id: 'arrow_2_up', label: 'Arrow_2_up', key: 'Arrow_2_up' },
      { id: 'arrow_2_down', label: 'Arrow_2_down', key: 'Arrow_2_down' },
      { id: 'arrow_2_left', label: 'Arrow_2_left', key: 'Arrow_2_left' },
      {
        id: 'arrow_2_right',
        label: 'Arrow_2_right',
        key: 'Arrow_2_right',
      },
      { id: 'arrow_3_up', label: 'Arrow_3_up', key: 'Arrow_3_up' },
      { id: 'arrow_3_down', label: 'Arrow_3_down', key: 'Arrow_3_down' },
      { id: 'arrow_3_left', label: 'Arrow_3_left', key: 'Arrow_3_left' },
      {
        id: 'arrow_3_right',
        label: 'Arrow_3_right',
        key: 'Arrow_3_right',
      },
      {
        id: 'arrow_3_right_up',
        label: 'Arrow_3_right_up',
        key: 'Arrow_3_right_up',
      },
      {
        id: 'arrow_3_right_down',
        label: 'Arrow_3_right_down',
        key: 'Arrow_3_right_down',
      },
      {
        id: 'arrow_3_left_up',
        label: 'Arrow_3_left_up',
        key: 'Arrow_3_left_up',
      },
      {
        id: 'arrow_3_left_down',
        label: 'Arrow_3_left_down',
        key: 'Arrow_3_left_down',
      },
      { id: 'arrows_up', label: 'Arrows_up', key: 'Arrows_up' },
      { id: 'arrows_down', label: 'Arrows_down', key: 'Arrows_down' },
      { id: 'arrows_left', label: 'Arrows_left', key: 'Arrows_left' },
      { id: 'arrows_right', label: 'Arrows_right', key: 'Arrows_right' },
      {
        id: 'arrows_up_down',
        label: 'Arrows_up_down',
        key: 'Arrows_up_down',
      },
    ],
  },
  {
    id: 'arrow_circles',
    title: 'Arrow Circles',
    icons: [
      {
        id: 'arrow_circle_up',
        label: 'Arrow_circle_up',
        key: 'Arrow_circle_up',
      },
      {
        id: 'arrow_circle_down',
        label: 'Arrow_circle_down',
        key: 'Arrow_circle_down',
      },
      {
        id: 'arrow_circle_left',
        label: 'Arrow_circle_left',
        key: 'Arrow_circle_left',
      },
      {
        id: 'arrow_circle_right',
        label: 'Arrow_circle_right',
        key: 'Arrow_circle_right',
      },
      {
        id: 'arrow_circle_top_right',
        label: 'Arrow_circle_top_right',
        key: 'Arrow_circle_top_right',
      },
      {
        id: 'arrow_circle_bottom_right',
        label: 'Arrow_circle_bottom_right',
        key: 'Arrow_circle_bottom_right',
      },
      {
        id: 'arrow_circle_bottom_left',
        label: 'Arrow_circle_bottom_left',
        key: 'Arrow_circle_bottom_left',
      },
      {
        id: 'arrow_circle_top_left',
        label: 'Arrow_circle_top_left',
        key: 'Arrow_circle_top_left',
      },
    ],
  },
  {
    id: 'navigation',
    title: 'Navigation',
    icons: [
      { id: 'refresh_cw', label: 'Refresh_CW', key: 'Refresh_CW' },
      { id: 'refresh_ccw', label: 'Refresh_CCW', key: 'Refresh_CCW' },
      { id: 'replace', label: 'Replace', key: 'Replace' },
      { id: 'next', label: 'Next', key: 'Next' },
      { id: 'back', label: 'Back', key: 'Back' },
      { id: 'back_2', label: 'Back_2', key: 'Back_2' },
      { id: 'back_3', label: 'Back_3', key: 'Back_3' },
      { id: 'next_2', label: 'Next_2', key: 'Next_2' },
      { id: 'next_3', label: 'Next_3', key: 'Next_3' },
    ],
  },
  {
    id: 'text_formatting',
    title: 'Text Formatting',
    icons: [
      { id: 'bold', label: 'Bold', key: 'Bold' },
      { id: 'italic', label: 'Italic', key: 'Italic' },
      { id: 'underline', label: 'Underline', key: 'Underline' },
      {
        id: 'strikethrough',
        label: 'Strikethrough',
        key: 'Strikethrough',
      },
      { id: 'bg_color', label: 'Bg_color', key: 'Bg_color' },
      { id: 'text_color', label: 'Text_color', key: 'Text_color' },
      { id: 'link', label: 'Link', key: 'Link' },
      { id: 'signature', label: 'Signature', key: 'Signature' },
      { id: 'align_left', label: 'Align_left', key: 'Align_left' },
      { id: 'align_right', label: 'Align_right', key: 'Align_right' },
      { id: 'align_center', label: 'Align_center', key: 'Align_center' },
      {
        id: 'align_justify',
        label: 'Align_justify',
        key: 'Align_justify',
      },
      { id: 'indent_left', label: 'Indent_left', key: 'Indent_left' },
      { id: 'indent_right', label: 'Indent_right', key: 'Indent_right' },
      { id: 'refine', label: 'Refine', key: 'Refine' },
      { id: 'write_ai', label: 'Write_ai', key: 'Write_ai' },
      { id: 'ai_wand', label: 'Ai_wand', key: 'Ai_wand' },
      { id: 'variable', label: 'Variable', key: 'Variable' },
      { id: 'enumerate', label: 'Enumerate', key: 'Enumerate' },
      { id: 'list', label: 'List', key: 'List' },
      { id: 'blockquote', label: 'Blockquote', key: 'Blockquote' },
      { id: 'emoji_smile', label: 'Emoji_smile', key: 'Emoji_smile' },
    ],
  },
  {
    id: 'status',
    title: 'Status',
    icons: [
      { id: 'info', label: 'Info', key: 'Info' },
      { id: 'attention', label: 'Attention', key: 'Attention' },
      { id: 'alert', label: 'Alert', key: 'Alert' },
      { id: 'close', label: 'Close', key: 'Close' },
      { id: 'checkmark', label: 'Checkmark', key: 'Checkmark' },
      { id: 'checkmark2', label: 'Checkmark2', key: 'Checkmark2' },
      {
        id: 'checkmark2_otl',
        label: 'Checkmark2_otl',
        key: 'Checkmark2_otl',
      },
      { id: 'check_in', label: 'Check_in', key: 'Check_in' },
      { id: 'check_out', label: 'Check_out', key: 'Check_out' },
      { id: 'star1', label: 'Star1', key: 'Star1' },
      { id: 'star2', label: 'Star2', key: 'Star2' },
    ],
  },
  {
    id: 'actions',
    title: 'Actions',
    icons: [
      { id: 'search', label: 'Search', key: 'Search' },
      { id: 'sort', label: 'Sort', key: 'Sort' },
      { id: 'location', label: 'Location', key: 'Location' },
      { id: 'tweak', label: 'Tweak', key: 'Tweak' },
      { id: 'settings', label: 'Settings', key: 'Settings' },
      { id: 'more1', label: 'More1', key: 'More1' },
      { id: 'more2', label: 'More2', key: 'More2' },
      { id: 'dot', label: 'Dot', key: 'Dot' },
      { id: 'drag', label: 'Drag', key: 'Drag' },
      { id: 'delete1', label: 'Delete1', key: 'Delete1' },
      { id: 'delete2', label: 'Delete2', key: 'Delete2' },
      { id: 'edit', label: 'Edit', key: 'Edit' },
      { id: 'copy', label: 'Copy', key: 'Copy' },
      { id: 'duplicate', label: 'Duplicate', key: 'Duplicate' },
      { id: 'move_to', label: 'Move_to', key: 'Move_to' },
      {
        id: 'move_to_folder',
        label: 'Move_to_folder',
        key: 'Move_to_folder',
      },
      {
        id: 'move_to_folder_2',
        label: 'Move_to_folder_2',
        key: 'Move_to_folder_2',
      },
      { id: 'print1', label: 'Print1', key: 'Print1' },
      { id: 'print2', label: 'Print2', key: 'Print2' },
      { id: 'pin', label: 'Pin', key: 'Pin' },
      { id: 'download', label: 'Download', key: 'Download' },
      { id: 'view', label: 'View', key: 'View' },
      { id: 'hide', label: 'Hide', key: 'Hide' },
      { id: 'grid', label: 'Grid', key: 'Grid' },
      { id: 'camera', label: 'Camera', key: 'Camera' },
      { id: 'save_icon', label: 'save_icon', key: 'save_icon' },
      { id: 'lock_icon', label: 'lock_icon', key: 'lock_icon' },
      { id: 'unlock_icon', label: 'unlock_icon', key: 'unlock_icon' },
    ],
  },
  {
    id: 'communication',
    title: 'Communication',
    icons: [
      {
        id: 'communications',
        label: 'Communications',
        key: 'Communications',
      },
      { id: 'call', label: 'Call', key: 'Call' },
      { id: 'call_check', label: 'Call_check', key: 'Call_check' },
      {
        id: 'ai_receptionist',
        label: 'ai_receptionist',
        key: 'ai_receptionist',
      },
      {
        id: 'arrow_missed_call',
        label: 'Arrow_missed_call',
        key: 'Arrow_missed_call',
      },
      {
        id: 'arrow_declined',
        label: 'Arrow_declined',
        key: 'Arrow_declined',
      },
      { id: 'message', label: 'Message', key: 'Message' },
      { id: 'fax', label: 'Fax', key: 'Fax' },
      { id: 'inbox', label: 'Inbox', key: 'Inbox' },
      { id: 'archive', label: 'Archive', key: 'Archive' },
      { id: 'attachment', label: 'Attachment', key: 'Attachment' },
      { id: 'sent', label: 'Sent', key: 'Sent' },
      { id: 'send', label: 'Send', key: 'Send' },
      {
        id: 'send_in_advance',
        label: 'Send_in_advance',
        key: 'Send_in_advance',
      },
      {
        id: 'send_in_advance_2',
        label: 'Send_in_advance_2',
        key: 'Send_in_advance_2',
      },
      { id: 'mail_fill', label: 'Mail_fill', key: 'Mail_fill' },
      { id: 'mail_line', label: 'Mail_line', key: 'Mail_line' },
      {
        id: 'mail_open_fill',
        label: 'Mail_open_fill',
        key: 'Mail_open_fill',
      },
      {
        id: 'mail_open_line',
        label: 'Mail_open_line',
        key: 'Mail_open_line',
      },
      { id: 'blocked', label: 'Blocked', key: 'Blocked' },
      { id: 'drafts', label: 'Drafts', key: 'Drafts' },
      { id: 'folder', label: 'Folder', key: 'Folder' },
      { id: 'tag', label: 'Tag', key: 'Tag' },
      {
        id: 'notifications',
        label: 'Notifications',
        key: 'Notifications',
      },
    ],
  },
  {
    id: 'audio',
    title: 'Audio',
    icons: [
      { id: 'record', label: 'Record', key: 'Record' },
      {
        id: 'audio_player_play',
        label: 'Audio_player_play',
        key: 'Audio_player_play',
      },
      {
        id: 'audio_player_pause',
        label: 'Audio_player_pause',
        key: 'Audio_player_pause',
      },
      {
        id: 'audio_player_play_2',
        label: 'Audio_player_play_2',
        key: 'Audio_player_play_2',
      },
      {
        id: 'audio_player_pause_2',
        label: 'Audio_player_pause_2',
        key: 'Audio_player_pause_2',
      },
      {
        id: 'audio_player_back_10',
        label: 'Audio_player_back_10',
        key: 'Audio_player_back_10',
      },
      {
        id: 'audio_player_forward_10',
        label: 'Audio_player_forward_10',
        key: 'Audio_player_forward_10',
      },
      {
        id: 'audio_player_volume',
        label: 'Audio_player_volume',
        key: 'Audio_player_volume',
      },
      {
        id: 'audio_player_volume_mute',
        label: 'Audio_player_volume_mute',
        key: 'Audio_player_volume_mute',
      },
    ],
  },
  {
    id: 'medical',
    title: 'Medical',
    icons: [
      { id: 'in_treatment', label: 'In_treatment', key: 'In_treatment' },
      {
        id: 'in_treatment_ai',
        label: 'In_treatment_ai',
        key: 'In_treatment_ai',
      },
      { id: 'intreatment2', label: 'InTreatment2', key: 'InTreatment2' },
      { id: 'stethoscope', label: 'Stethoscope', key: 'Stethoscope' },
      {
        id: 'health_condition',
        label: 'Health_condition',
        key: 'Health_condition',
      },
      { id: 'allergies', label: 'Allergies', key: 'Allergies' },
      { id: 'health', label: 'Health', key: 'Health' },
      {
        id: 'medical_test',
        label: 'Medical_test',
        key: 'Medical_test',
      },
      { id: 'lab', label: 'Lab', key: 'Lab' },
      { id: 'health_test', label: 'Health_test', key: 'Health_test' },
      {
        id: 'attention_required',
        label: 'Attention_required',
        key: 'Attention_required',
      },
    ],
  },
  {
    id: 'finance',
    title: 'Finance',
    icons: [
      { id: 'money', label: 'Money', key: 'Money' },
      { id: 'money2', label: 'Money2', key: 'Money2' },
      { id: 'dollar', label: 'Dollar', key: 'Dollar' },
      {
        id: 'adjustment',
        label: 'Adjustment',
        key: 'Adjustment',
      },
      { id: 'claims', label: 'Claims', key: 'Claims' },
      { id: 'wallet', label: 'Wallet', key: 'Wallet' },
      { id: 'collect1', label: 'Collect1', key: 'Collect1' },
      { id: 'collect2', label: 'Collect2', key: 'Collect2' },
      {
        id: 'missed_payments',
        label: 'Missed_payments',
        key: 'Missed_payments',
      },
      {
        id: 'recurring',
        label: 'Recurring',
        key: 'Recurring',
      },
    ],
  },
  {
    id: 'insurance',
    title: 'Insurance',
    icons: [
      {
        id: 'insurance_module',
        label: 'Insurance_module',
        key: 'Insurance_module',
      },
      { id: 'shield', label: 'Shield', key: 'Shield' },
      {
        id: 'shield_outline',
        label: 'Shield_outline',
        key: 'Shield_outline',
      },
      {
        id: 'insurance_verified',
        label: 'Insurance_verified',
        key: 'Insurance_verified',
      },
      {
        id: 'insurance_plus',
        label: 'Insurance_plus',
        key: 'Insurance_plus',
      },
      {
        id: 'insurance_unverified',
        label: 'Insurance_unverified',
        key: 'Insurance_unverified',
      },
      {
        id: 'insurance_expired',
        label: 'Insurance_expired',
        key: 'Insurance_expired',
      },
      {
        id: 'insurance_info',
        label: 'Insurance_info',
        key: 'Insurance_info',
      },
    ],
  },
  {
    id: 'people',
    title: 'People',
    icons: [
      { id: 'staff', label: 'Staff', key: 'Staff' },
      { id: 'user_avatar', label: 'User_avatar', key: 'User_avatar' },
      {
        id: 'avatar_clock',
        label: 'Avatar_clock',
        key: 'Avatar_clock',
      },
      {
        id: 'avatar_user_info',
        label: 'Avatar_user_info',
        key: 'Avatar_user_info',
      },
      {
        id: 'add_user_avatar',
        label: 'Add_user_avatar',
        key: 'Add_user_avatar',
      },
      {
        id: 'add_group_avatar',
        label: 'Add_group_avatar',
        key: 'Add_group_avatar',
      },
      { id: 'male', label: 'Male', key: 'Male' },
      { id: 'female', label: 'Female', key: 'Female' },
      { id: 'attendance', label: 'Attendance', key: 'Attendance' },
      {
        id: 'avatar_blocked',
        label: 'Avatar_blocked',
        key: 'Avatar_blocked',
      },
      { id: 'patient_info', label: 'Patient_info', key: 'Patient_info' },
    ],
  },
  {
    id: 'documents',
    title: 'Documents',
    icons: [
      { id: 'file', label: 'File', key: 'File' },
      { id: 'document', label: 'Document', key: 'Document' },
      { id: 'document2', label: 'Document2', key: 'Document2' },
      { id: 'files', label: 'Files', key: 'Files' },
      { id: 'file_2', label: 'File_2', key: 'File_2' },
      { id: 'files_pdf', label: 'Files_pdf', key: 'Files_pdf' },
      {
        id: 'files_letters',
        label: 'Files_letters',
        key: 'Files_letters',
      },
      { id: 'notes', label: 'Notes', key: 'Notes' },
      { id: 'charting', label: 'Charting', key: 'Charting' },
      { id: 'image_viewer', label: 'Image_viewer', key: 'Image_viewer' },
      {
        id: 'form_builder',
        label: 'Form_builder',
        key: 'Form_builder',
      },
      {
        id: 'form_completed',
        label: 'Form_completed',
        key: 'Form_completed',
      },
      {
        id: 'form_incomplete',
        label: 'Form_incomplete',
        key: 'Form_incomplete',
      },
      { id: 'tx_plan', label: 'Tx_plan', key: 'Tx_plan' },
      { id: 'tx_plan2', label: 'Tx_plan2', key: 'Tx_plan2' },
      { id: 'billing', label: 'Billing', key: 'Billing' },
      {
        id: 'billing_adjustment',
        label: 'Billing_adjustment',
        key: 'Billing_adjustment',
      },
      { id: 'invoice', label: 'Invoice', key: 'Invoice' },
      { id: 'membership', label: 'Membership', key: 'Membership' },
      { id: 'reporting', label: 'Reporting', key: 'Reporting' },
      { id: 'campaign', label: 'Campaign', key: 'Campaign' },
      // Inline SVG: <img src="…Dashboard…"> is often blocked by ad blockers.
      { id: 'dashboard', label: 'Dashboard', key: 'Dashboard', useIcon: true },
    ],
  },
  {
    id: 'time',
    title: 'Time',
    icons: [
      { id: 'clock', label: 'Clock', key: 'Clock' },
      { id: 'clock2', label: 'Clock2', key: 'Clock2' },
      { id: 'history', label: 'History', key: 'History' },
      { id: 'request', label: 'Request', key: 'Request' },
    ],
  },
  {
    id: 'calendar',
    title: 'Schedule',
    icons: [
      { id: 'add_schedule', label: 'Add_schedule', key: 'Add_schedule' },
      { id: 'schedule', label: 'Schedule', key: 'Schedule' },
      { id: 'schedule2', label: 'Schedule2', key: 'Schedule2' },
      { id: 'scheduler', label: 'Scheduler', key: 'Scheduler' },
      {
        id: 'previous_calendar',
        label: 'Previous_calendar',
        key: 'Previous_calendar',
      },
      {
        id: 'upcoming_calendar',
        label: 'Upcoming_calendar',
        key: 'Upcoming_calendar',
      },
      { id: 'calendar', label: 'Calendar', key: 'Calendar' },
      {
        id: 'add_schedule_otl',
        label: 'Add_schedule_otl',
        key: 'Add_schedule_otl',
      },
      { id: 'schedule_otl', label: 'Schedule_otl', key: 'Schedule_otl' },
      {
        id: 'previous_calendar_otl',
        label: 'Previous_calendar_otl',
        key: 'Previous_calendar_otl',
      },
      {
        id: 'upcoming_calendar_otl',
        label: 'Upcoming_calendar_otl',
        key: 'Upcoming_calendar_otl',
      },
      { id: 'calendar_otl', label: 'Calendar_otl', key: 'Calendar_otl' },
    ],
  },
]

const IconsShowcase: React.FC = () => (
  <UiPreviewCanvas
    title="Icons"
    description="System icons from @enamelai/design-system/icons."
    stageClassName="ui-preview__stage--narrow icons-showcase__stage-wrap"
  >
    <div className="icons-showcase__sheet">
      {ICON_GROUPS.map((group) => (
        <section key={group.id} className="icons-showcase__group">
          <h4 className="icons-showcase__group-title">{group.title}</h4>
          <div className="icons-showcase__group-row">
            {group.icons.map((icon) => {
              const entry = icons[icon.key]
              const InlineIcon = icon.useIcon ? entry.Icon : undefined
              return (
                <div key={icon.id} className="icons-showcase__card">
                  <div className="icons-showcase__swatch">
                    {InlineIcon ? (
                      <InlineIcon aria-hidden="true" width={40} height={40} />
                    ) : (
                      <img
                        src={entry.src}
                        alt={icon.label}
                        width={40}
                        height={40}
                      />
                    )}
                  </div>
                  <p className="icons-showcase__label">{icon.label}</p>
                </div>
              )
            })}
          </div>
        </section>
      ))}
    </div>
  </UiPreviewCanvas>
)

export default IconsShowcase
